import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, HStack, Spinner, Text, VStack } from '@chakra-ui/react';
import { useSetAtom } from 'jotai';
import { useAuth } from '../hooks/useAuth';
import { api, ApiError } from '../lib/api';
import { currentFamilyIdAtom, setAuthAtom } from '../store';
import { WelcomeShell } from '../components/page/WelcomeShell';
import { useT, type MessageKey } from '../i18n';

// An app reviewer's review link, /review/:token (M56, D203): signs this
// browser in as a demo account without a passkey, then shows the app. The
// app makes this device's encryption key on first open, as after any sign-in.

type Step =
  | { name: 'signingIn' }
  | { name: 'signedInElsewhere'; who: string }
  | { name: 'failed'; message: MessageKey; retry: boolean };

function failureFor(err: unknown): { message: MessageKey; retry: boolean } {
  if (err instanceof ApiError) {
    if (err.code === 'LINK_EXPIRED')
      return { message: 'account.review.expired', retry: false };
    if (err.code === 'LINK_REVOKED')
      return { message: 'account.review.revoked', retry: false };
    if (err.code === 'LINK_NOT_FOUND' || err.code === 'NOT_A_DEMO_ACCOUNT')
      return { message: 'account.review.invalid', retry: false };
  }
  return { message: 'account.review.failed', retry: true };
}

export function ReviewLinkPage() {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const t = useT();
  const { currentUser, isAuthenticated, logout } = useAuth();
  const setAuth = useSetAtom(setAuthAtom);
  const setCurrentFamilyId = useSetAtom(currentFamilyIdAtom);
  // Someone already signed in here isn't signed out without asking.
  const [step, setStep] = useState<Step>(
    isAuthenticated
      ? { name: 'signedInElsewhere', who: currentUser?.displayName ?? '' }
      : { name: 'signingIn' }
  );
  const started = useRef(false);

  const signIn = useCallback(async () => {
    setStep({ name: 'signingIn' });
    try {
      const res = await api.openReviewLink(token);
      setAuth({ user: res.data.user, tokens: res.data.tokens });
      setCurrentFamilyId(res.data.familyId);
      navigate('/', { replace: true });
    } catch (err) {
      setStep({ name: 'failed', ...failureFor(err) });
    }
  }, [navigate, setAuth, setCurrentFamilyId, token]);

  useEffect(() => {
    // Once, even under StrictMode's double effects.
    if (started.current || step.name !== 'signingIn') return;
    started.current = true;
    void signIn();
  }, [signIn, step.name]);

  const switchAccount = async () => {
    await logout().catch(() => undefined);
    started.current = true;
    void signIn();
  };

  return (
    <WelcomeShell title={t('account.review.title')}>
      {step.name === 'signingIn' && (
        <HStack spacing={3} justify="center" py={6} role="status">
          <Spinner size="sm" color="brand.500" />
          <Text color="muted">{t('account.review.signingIn')}</Text>
        </HStack>
      )}
      {step.name === 'signedInElsewhere' && (
        <VStack spacing={4} align="stretch">
          <Text color="muted">
            {t('account.review.signedInAs', { name: step.who })}
          </Text>
          <Button colorScheme="brand" onClick={() => void switchAccount()}>
            {t('account.review.switch')}
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate('/', { replace: true })}
          >
            {t('account.review.stay')}
          </Button>
        </VStack>
      )}
      {step.name === 'failed' && (
        <VStack spacing={4} align="stretch">
          <Text color="muted" role="alert">
            {t(step.message)}
          </Text>
          {step.retry && (
            <Button colorScheme="brand" onClick={() => void signIn()}>
              {t('account.review.tryAgain')}
            </Button>
          )}
          <Button
            variant="ghost"
            onClick={() => navigate('/', { replace: true })}
          >
            {t('account.review.home')}
          </Button>
        </VStack>
      )}
    </WelcomeShell>
  );
}
